import {
  breakpointSizes,
  getBreakpointDown,
  getFontCss,
  HoverableLinkSizes,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import Button from 'ext-ui/components/Button/Button'
import React from 'react'
import { useDispatch } from 'react-redux'
import AppName from 'src/components/AppName/AppName'
import Box from 'src/components/Box/Box'
import ResourceCenterLink from 'src/components/ResourceCenterLink/ResourceCenterLink'
import { useTranslate } from 'src/localization/Trns'
import { useQueryStatistics } from 'src/queries/statistics/statistics.queries'
import { StatisticsKeys } from 'src/queries/statistics/statistics.queries.constants'
import { createSetIsDeleteAccountPopupOpen } from 'src/redux/actions/app.actions'
import styled, { css } from 'styled-components'

interface DeleteProfileBoxProps {
  className?: string
  deletingUser: boolean
  topLevelError?: string
  deleteProfile: () => void
}

const StyledP = styled.p`
  ${getFontCss(
    16,
    themeConstants.colors.font_primary,
    styleConstants.fontFamilies.regular,
  )}
  margin-top: 30px;
`

const DeleteProfileBox: React.FC<DeleteProfileBoxProps> = (
  props,
): React.ReactElement => {
  const translate = useTranslate()

  const dispatch = useDispatch()
  const { results: statisticsResults } = useQueryStatistics({
    statisticsKeys: [
      StatisticsKeys.CreditCardsActiveSourceCount,
      StatisticsKeys.VirtualCardsActiveSourceUserOrViewerOrDelegateUserMeCount,
      StatisticsKeys.CreditCardsActiveDelegateCount,
    ] as const,
  })
  const activeAccounts =
    statisticsResults[StatisticsKeys.CreditCardsActiveSourceCount].data ?? 0
  const activeVCNs =
    statisticsResults[
      StatisticsKeys.VirtualCardsActiveSourceUserOrViewerOrDelegateUserMeCount
    ].data ?? 0
  const activeBudgets =
    statisticsResults[StatisticsKeys.CreditCardsActiveDelegateCount].data ?? 0
  // Deliberate tightening vs legacy: the Redux counts initialized to 0, so the
  // destructive delete affordance could show before the stats ever loaded. Gate
  // on every query's isSuccess (not !anyLoading): an errored or disabled fetch
  // leaves isLoading false while data stays undefined, so !anyLoading would let
  // the defaulted-0 counts fall through to the "safe to delete" UI.
  const allStatisticsLoaded =
    statisticsResults[StatisticsKeys.CreditCardsActiveSourceCount].isSuccess &&
    statisticsResults[
      StatisticsKeys.VirtualCardsActiveSourceUserOrViewerOrDelegateUserMeCount
    ].isSuccess &&
    statisticsResults[StatisticsKeys.CreditCardsActiveDelegateCount].isSuccess
  const canDelete =
    allStatisticsLoaded && activeAccounts + activeVCNs + activeBudgets === 0

  return (
    <Box
      css={css`
        display: flex;
        flex-direction: column;
        background-color: $color-gray-lightest;
      `}
    >
      <span
        css={css`
          ${getFontCss(
            20,
            styleConstants.colors.red,
            styleConstants.fontFamilies.medium,
          )}
        `}
      >
        {translate('Delete Your Profile')}
      </span>
      <StyledP>
        {translate(
          'In order to delete your ${appName} Profile, you must have had no virtual cards, budgets, or registered accounts ever connected to your profile.',
          {
            appName: <AppName />,
          },
        )}
      </StyledP>
      {props.topLevelError !== undefined && (
        <span
          css={css`
            ${getFontCss(
              14,
              styleConstants.colors.red,
              styleConstants.fontFamilies.regular,
            )}
            margin-top: 40px;
            ${getBreakpointDown(breakpointSizes.XSM)(css`
              margin-top: 30px;
            `)}
          `}
        >
          {props.topLevelError}
        </span>
      )}

      {canDelete ? (
        <Button
          css={css`
            margin-top: 30px;
            align-self: flex-start;
          `}
          type="button"
          variant="severe"
          onClick={(): void => {
            dispatch(createSetIsDeleteAccountPopupOpen(true))
          }}
          loading={props.deletingUser}
          disabled={props.deletingUser}
        >
          {translate('Permanently Delete Profile')}
        </Button>
      ) : (
        <span
          css={css`
            ${getFontCss(
              12,
              styleConstants.colors.font_gray_light,
              styleConstants.fontFamilies.regular,
            )}
            margin-top: 33px;
          `}
        >
          {translate(
            'Please contact us via our ${resourceCenter} to complete the deletion of your account.',
            {
              resourceCenter: (
                <ResourceCenterLink underline size={HoverableLinkSizes.Small} />
              ),
            },
          )}
        </span>
      )}
    </Box>
  )
}

export default DeleteProfileBox
